/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {animate, state, style, transition, trigger} from '@angular/animations';
import {CdkStep, CdkStepper} from '@angular/cdk/stepper';
import {
  Component,
  ContentChild,
  ContentChildren,
  Directive,
  ElementRef,
  forwardRef,
  Inject,
  Optional,
  QueryList,
  SkipSelf,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import {FormControl, FormGroupDirective, NgForm} from '@angular/forms';
import {
  defaultErrorStateMatcher,
  ErrorOptions,
  ErrorStateMatcher, MATERIAL_COMPATIBILITY_MODE,
  MD_ERROR_GLOBAL_OPTIONS,
} from '@angular/material/core';
import {MdStepHeader} from './step-header';
import {MdStepLabel} from './step-label';
/**
 * Workaround for https://github.com/angular/angular/issues/17849
 */
export const _MdStep = CdkStep;
export const /** @type {?} */ _MdStepper = CdkStepper;
export class MdStep extends _MdStep implements ErrorOptions {
/**
 * Content for step label given by <ng-template matStepLabel> or <ng-template mdStepLabel>.
 */
stepLabel: MdStepLabel;
/**
 * Original ErrorStateMatcher that checks the validity of form control.
 */
private _originalErrorStateMatcher: ErrorStateMatcher;
/**
 * @param {?} mdStepper
 * @param {?} errorOptions
 */
constructor( mdStepper: MdStepper,
                 errorOptions: ErrorOptions) {
    super(mdStepper);
    if (errorOptions && errorOptions.errorStateMatcher) {
      this._originalErrorStateMatcher = errorOptions.errorStateMatcher;
    } else {
      this._originalErrorStateMatcher = defaultErrorStateMatcher;
    }
  }
/**
 * Custom error state matcher that additionally checks for validity of interacted form.
 */
errorStateMatcher = (control: FormControl, form: FormGroupDirective | NgForm) => {
    let originalErrorState = this._originalErrorStateMatcher(control, form);

    // Custom error state checks for the validity of form that is not submitted or touched
    // since user can trigger a form change by calling for another step without directly
    // interacting with the current form.
    let customErrorState =  control.invalid && this.interacted;

    return originalErrorState || customErrorState;
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-step, mat-step',
  templateUrl: 'step.html',
  providers: [{provide: MD_ERROR_GLOBAL_OPTIONS, useExisting: MdStep}],
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: MdStepper, decorators: [{ type: Inject, args: [forwardRef(() => MdStepper), ] }, ]},
{type: undefined, decorators: [{ type: Optional }, { type: SkipSelf }, { type: Inject, args: [MD_ERROR_GLOBAL_OPTIONS, ] }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'stepLabel': [{ type: ContentChild, args: [MdStepLabel, ] },],
};
}

function MdStep_tsickle_Closure_declarations() {
/** @type {?} */
MdStep.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdStep.ctorParameters;
/** @type {?} */
MdStep.propDecorators;
/**
 * Content for step label given by <ng-template matStepLabel> or <ng-template mdStepLabel>.
 * @type {?}
 */
MdStep.prototype.stepLabel;
/**
 * Original ErrorStateMatcher that checks the validity of form control.
 * @type {?}
 */
MdStep.prototype._originalErrorStateMatcher;
/**
 * Custom error state matcher that additionally checks for validity of interacted form.
 * @type {?}
 */
MdStep.prototype.errorStateMatcher;
}

export class MdStepper extends _MdStepper {
/**
 * The list of step headers of the steps in the stepper.
 */
_stepHeader: QueryList<ElementRef>;
/**
 * Steps that the stepper holds.
 */
_steps: QueryList<MdStep>;
static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: '[mdStepper]'
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'_stepHeader': [{ type: ViewChildren, args: [MdStepHeader, {read: ElementRef}, ] },],
'_steps': [{ type: ContentChildren, args: [MdStep, ] },],
};
}

function MdStepper_tsickle_Closure_declarations() {
/** @type {?} */
MdStepper.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdStepper.ctorParameters;
/** @type {?} */
MdStepper.propDecorators;
/**
 * The list of step headers of the steps in the stepper.
 * @type {?}
 */
MdStepper.prototype._stepHeader;
/**
 * Steps that the stepper holds.
 * @type {?}
 */
MdStepper.prototype._steps;
}

export class MdHorizontalStepper extends MdStepper { static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-horizontal-stepper, mat-horizontal-stepper',
  templateUrl: 'stepper-horizontal.html',
  styleUrls: ['stepper.css'],
  inputs: ['selectedIndex'],
  host: {
    'class': 'mat-stepper-horizontal',
    'role': 'tablist',
  },
  animations: [
    trigger('stepTransition', [
      state('previous', style({transform: 'translate3d(-100%, 0, 0)', visibility: 'hidden'})),
      state('current', style({transform: 'translate3d(0%, 0, 0)', visibility: 'visible'})),
      state('next', style({transform: 'translate3d(100%, 0, 0)', visibility: 'hidden'})),
      transition('* => *',
          animate('500ms cubic-bezier(0.35, 0, 0.25, 1)'))
    ])
  ],
  providers: [{provide: MdStepper, useExisting: MdHorizontalStepper}],
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  viewProviders: [{provide: MATERIAL_COMPATIBILITY_MODE, useValue: true}],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdHorizontalStepper_tsickle_Closure_declarations() {
/** @type {?} */
MdHorizontalStepper.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdHorizontalStepper.ctorParameters;
}

export class MdVerticalStepper extends MdStepper { static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-vertical-stepper, mat-vertical-stepper',
  templateUrl: 'stepper-vertical.html',
  styleUrls: ['stepper.css'],
  inputs: ['selectedIndex'],
  host: {
    'class': 'mat-stepper-vertical',
    'role': 'tablist',
  },
  animations: [
    trigger('stepTransition', [
      state('previous', style({height: '0px', visibility: 'hidden'})),
      state('next', style({height: '0px', visibility: 'hidden'})),
      state('current', style({height: '*', visibility: 'visible'})),
      transition('* <=> current', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ],
  providers: [{provide: MdStepper, useExisting: MdVerticalStepper}],
  viewProviders: [{provide: MATERIAL_COMPATIBILITY_MODE, useValue: true}],
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function MdVerticalStepper_tsickle_Closure_declarations() {
/** @type {?} */
MdVerticalStepper.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdVerticalStepper.ctorParameters;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
